import { useState, useEffect, useMemo, useCallback } from 'react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { FilterableSelect, type FilterableSelectOption } from '@/components/ui/filterable-select'
import { Save } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  useAccounts,
  useAccountAnalytics,
  useOpenItems,
  useUpdateMatch,
} from './hooks/useReconApi'
import type {
  Counterpart,
  BankAccount,
  Match,
  APXFieldValue,
  OpenItem,
  Transaction,
} from './types'

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

interface CounterpartEditorProps {
  match?: Match
  transaction: Transaction
  clientId: string
  bankAccountId: number
  bankAccountLabel: string
  onConfirm?: (counterparts: CounterpartPayload[]) => void
  onChange?: (counterparts: CounterpartPayload[]) => void
  hideSave?: boolean
}

export interface CounterpartPayload {
  counterpart_kind: 'mirage_open_item' | 'gl_account_post' | 'bank_account_transfer'
  dt_account_id: number
  ct_account_id: number
  dt_apx_id?: number
  ct_apx_id?: number
  amount: string
  note?: string
  doc_nom?: string
  doc_date?: string
  dt_apx_part?: string
  ct_apx_part?: string
}

// ---------------------------------------------------------------------------
// Editable state
// ---------------------------------------------------------------------------

interface EditState {
  dtAccountId: number
  ctAccountId: number
  dtApxId: number
  ctApxId: number
  amount: string
  note: string
  docNom: string
  docDate: string
  counterpartKind: string
  dtApxFields?: APXFieldValue[]
  ctApxFields?: APXFieldValue[]
}

function fromCounterpart(cp: Counterpart): EditState {
  return {
    dtAccountId: cp.dt_account_id,
    ctAccountId: cp.ct_account_id,
    dtApxId: cp.dt_apx_id || 0,
    ctApxId: cp.ct_apx_id || 0,
    amount: cp.amount,
    note: cp.note || '',
    docNom: cp.doc_nom || '',
    docDate: cp.doc_date || '',
    counterpartKind: cp.counterpart_kind,
    dtApxFields: cp.dt_apx_fields,
    ctApxFields: cp.ct_apx_fields,
  }
}

function emptyState(bankAccountId: number, isOutgoing: boolean, amount: number): EditState {
  return {
    dtAccountId: isOutgoing ? 0 : bankAccountId,
    ctAccountId: isOutgoing ? bankAccountId : 0,
    dtApxId: 0,
    ctApxId: 0,
    amount: Math.abs(amount).toFixed(2),
    note: '',
    docNom: '',
    docDate: '',
    counterpartKind: 'gl_account_post',
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CounterpartEditor({
  match,
  transaction,
  clientId,
  bankAccountId,
  bankAccountLabel,
  onConfirm,
  onChange,
  hideSave,
}: CounterpartEditorProps) {
  const { data: accounts = [] } = useAccounts(clientId)
  const updateMatch = useUpdateMatch()
  const isOutgoing = transaction.amount < 0

  const [lines, setLines] = useState<EditState[]>(() =>
    match && match.counterparts.length > 0
      ? match.counterparts.map(fromCounterpart)
      : [emptyState(bankAccountId, isOutgoing, transaction.amount)]
  )
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    if (match && match.counterparts.length > 0) {
      setLines(match.counterparts.map(fromCounterpart))
      setDirty(false)
    }
  }, [match?.id, match?.counterparts.length])

  const updateLine = (idx: number, patch: Partial<EditState>) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)))
    setDirty(true)
  }

  const buildPayloads = (): CounterpartPayload[] =>
    lines.map((l) => ({
      counterpart_kind: l.counterpartKind as CounterpartPayload['counterpart_kind'],
      dt_account_id: l.dtAccountId,
      ct_account_id: l.ctAccountId,
      dt_apx_id: l.dtApxId || undefined,
      ct_apx_id: l.ctApxId || undefined,
      amount: l.amount,
      note: l.note || undefined,
      doc_nom: l.docNom || undefined,
      doc_date: l.docDate || undefined,
    }))

  useEffect(() => {
    if (onChange) onChange(buildPayloads())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines])

  const handleSave = () => {
    if (onConfirm) {
      onConfirm(buildPayloads())
      return
    }
    if (!match) return
    updateMatch.mutate(
      {
        id: match.id,
        client_id: clientId,
        match_kind: match.match_kind,
        counterparts: buildPayloads(),
      },
      { onSuccess: () => setDirty(false) }
    )
  }

  const accountOptions: FilterableSelectOption[] = useMemo(() =>
    accounts.map((a) => ({
      value: String(a.id),
      label: a.name,
      details: `${a.code}${a.sub_code ? `.${a.sub_code}` : ''}`,
      searchText: `${a.code} ${a.sub_code || ''} ${a.name}`,
    })),
    [accounts]
  )

  const renderAccountOption = useCallback((opt: FilterableSelectOption) => (
    <div className="flex items-center gap-2">
      <OptionBadge color="blue">{opt.details}</OptionBadge>
      <span className="truncate">{opt.label}</span>
    </div>
  ), [])

  return (
    <div className="space-y-3">
      {lines.map((line, idx) => (
        <SingleLine
          key={idx}
          line={line}
          accountOptions={accountOptions}
          renderAccountOption={renderAccountOption}
          accounts={accounts}
          bankAccountId={bankAccountId}
          bankAccountLabel={bankAccountLabel}
          currency={transaction.currency}
          clientId={clientId}
          onChange={(patch) => updateLine(idx, patch)}
        />
      ))}

      {!hideSave && (
        <div className="flex items-center gap-2">
          {dirty && (
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5"
              disabled={updateMatch.isLoading}
              onClick={handleSave}
            >
              <Save className="h-3.5 w-3.5" />
              {updateMatch.isLoading ? 'Saving...' : 'Save changes'}
            </Button>
          )}
          {updateMatch.isError && (
            <span className="text-sm text-red-600">
              {(updateMatch.error as Error).message}
            </span>
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Single counterpart line
// ---------------------------------------------------------------------------

function SingleLine({
  line,
  accountOptions,
  renderAccountOption,
  accounts,
  bankAccountId,
  bankAccountLabel,
  currency,
  clientId,
  onChange,
}: {
  line: EditState
  accountOptions: FilterableSelectOption[]
  renderAccountOption: (opt: FilterableSelectOption) => React.ReactNode
  accounts: BankAccount[]
  bankAccountId: number
  bankAccountLabel: string
  currency: string
  clientId: string
  onChange: (patch: Partial<EditState>) => void
}) {
  return (
    <div className="rounded-md border border-neutral-200 bg-white">
      <div className="grid grid-cols-2 divide-x divide-neutral-200">
        <AccountPanel
          label="Debit account / Partner"
          accountId={line.dtAccountId}
          apxId={line.dtApxId}
          apxFields={line.dtApxFields}
          docNom={line.docNom}
          docDate={line.docDate}
          accountOptions={accountOptions}
          renderAccountOption={renderAccountOption}
          accounts={accounts}
          bankAccountId={bankAccountId}
          bankAccountLabel={bankAccountLabel}
          clientId={clientId}
          onAccountChange={(id) => onChange({ dtAccountId: id, dtApxId: 0, dtApxFields: undefined, docNom: '', docDate: '' })}
          onApxSelect={(apxId, oi) =>
            onChange({
              dtApxId: apxId,
              dtApxFields: undefined,
              docNom: oi?.doc_number || line.docNom,
              docDate: oi?.doc_date || line.docDate,
              counterpartKind: apxId ? 'mirage_open_item' : line.counterpartKind,
            })
          }
          onDocChange={(docNom, docDate) => onChange({ docNom, docDate })}
        />
        <AccountPanel
          label="Credit account / Partner"
          accountId={line.ctAccountId}
          apxId={line.ctApxId}
          apxFields={line.ctApxFields}
          docNom={line.docNom}
          docDate={line.docDate}
          accountOptions={accountOptions}
          renderAccountOption={renderAccountOption}
          accounts={accounts}
          bankAccountId={bankAccountId}
          bankAccountLabel={bankAccountLabel}
          clientId={clientId}
          onAccountChange={(id) => onChange({ ctAccountId: id, ctApxId: 0, ctApxFields: undefined, docNom: '', docDate: '' })}
          onApxSelect={(apxId, oi) =>
            onChange({
              ctApxId: apxId,
              ctApxFields: undefined,
              docNom: oi?.doc_number || line.docNom,
              docDate: oi?.doc_date || line.docDate,
              counterpartKind: apxId ? 'mirage_open_item' : line.counterpartKind,
            })
          }
          onDocChange={(docNom, docDate) => onChange({ docNom, docDate })}
        />
      </div>

      <div className="space-y-1 border-t border-neutral-200 px-3 py-2">
        <div className="flex items-center gap-2">
          <span className="w-20 shrink-0 text-xs text-neutral-400">Note:</span>
          <ClickToEdit
            value={line.note}
            onChange={(v) => onChange({ note: v })}
            placeholder="Click to add note..."
            maxLength={200}
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="w-20 shrink-0 text-xs text-neutral-400">Amount:</span>
          <ClickToEdit
            value={line.amount}
            onChange={(v) => onChange({ amount: v })}
            placeholder="0.00"
            type="number"
            className="w-auto font-mono font-semibold"
          />
          <span className="ml-1 text-xs text-neutral-400">{currency}</span>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Account panel with analytics
// ---------------------------------------------------------------------------

function AccountPanel({
  label,
  accountId,
  apxId,
  apxFields,
  docNom,
  docDate,
  accountOptions,
  renderAccountOption,
  accounts,
  clientId,
  onAccountChange,
  onApxSelect,
  onDocChange,
}: {
  label: string
  accountId: number
  apxId: number
  apxFields?: APXFieldValue[]
  docNom: string
  docDate: string
  accountOptions: FilterableSelectOption[]
  renderAccountOption: (opt: FilterableSelectOption) => React.ReactNode
  accounts: BankAccount[]
  bankAccountId?: number
  bankAccountLabel?: string
  clientId: string
  onAccountChange: (id: number) => void
  onApxSelect: (apxId: number, oi?: OpenItem) => void
  onDocChange: (docNom: string, docDate: string) => void
}) {
  const account = accounts.find((a) => a.id === accountId)

  return (
    <div className="p-3">
      <div className="mb-2 text-[11px] font-medium uppercase tracking-wide text-neutral-400">
        {label}
      </div>

      <FilterableSelect
        value={accountId > 0 ? String(accountId) : undefined}
        onValueChange={(v) => onAccountChange(Number(v))}
        options={accountOptions}
        placeholder="Select account..."
        searchPlaceholder="Search account..."
        triggerClassName="h-8 text-sm border-0 shadow-none px-1 hover:bg-neutral-100"
        renderOption={renderAccountOption}
        renderSelectedValue={(opt) => (
          <div className="flex items-center gap-2">
            <OptionBadge color="blue">{opt.details}</OptionBadge>
            <span className="truncate">{opt.label}</span>
          </div>
        )}
      />

      {account && account.has_analytic && (
        <AnalyticsSection
          clientId={clientId}
          accountId={accountId}
          apxId={apxId}
          apxFields={apxFields}
          docNom={docNom}
          docDate={docDate}
          onApxSelect={onApxSelect}
          onDocChange={onDocChange}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Analytics — Доставчик / Фактура / Дата
// ---------------------------------------------------------------------------

function AnalyticsSection({
  clientId,
  accountId,
  apxId,
  docNom,
  docDate,
  onApxSelect,
  onDocChange,
}: {
  clientId: string
  accountId: number
  apxId: number
  apxFields?: APXFieldValue[]
  docNom: string
  docDate: string
  onApxSelect: (apxId: number, oi?: OpenItem) => void
  onDocChange: (docNom: string, docDate: string) => void
}) {
  const { data: def } = useAccountAnalytics(clientId, accountId)
  const { data: openItems = [] } = useOpenItems(clientId, [accountId])

  const [selectedCounterpartyId, setSelectedCounterpartyId] = useState<number | null>(null)

  useEffect(() => {
    if (apxId > 0) {
      const oi = openItems.find((o) => o.apx_id === apxId)
      if (oi?.counterparty_id) setSelectedCounterpartyId(oi.counterparty_id)
    }
  }, [apxId, openItems])

  const currentOi = openItems.find((o) => o.apx_id === apxId)

  // Build FilterableSelect options for each field
  const counterpartyOptions: FilterableSelectOption[] = useMemo(() => {
    if (!def || def.fields.length === 0) return []
    const field = def.fields[0]
    if (!field.is_lookup || !field.options) return []
    return [...field.options]
      .sort((a, b) => a.id - b.id)
      .map((o) => ({
        value: String(o.id),
        label: o.description,
        details: o.code,
        searchText: `${o.code} ${o.description}`,
      }))
  }, [def])

  const renderCounterpartyOption = useCallback((opt: FilterableSelectOption) => (
    <div className="flex items-center gap-2">
      <OptionBadge color="green">{opt.details}</OptionBadge>
      <span className="truncate">{opt.label}</span>
    </div>
  ), [])

  const counterpartyOpenItems = useMemo(() => {
    if (!selectedCounterpartyId) return openItems
    return openItems.filter((oi) => oi.counterparty_id === selectedCounterpartyId)
  }, [openItems, selectedCounterpartyId])

  const invoiceOptions: FilterableSelectOption[] = useMemo(() =>
    counterpartyOpenItems.map((oi) => ({
      value: String(oi.apx_id),
      label: `${oi.counterparty_name || ''} ${oi.doc_date || ''}`.trim(),
      details: oi.doc_number || `APX ${oi.apx_id}`,
      searchText: `${oi.doc_number} ${oi.counterparty_name} ${oi.doc_date} ${oi.balance}`,
    })),
    [counterpartyOpenItems]
  )

  const renderInvoiceOption = useCallback((opt: FilterableSelectOption) => {
    const oi = counterpartyOpenItems.find((o) => String(o.apx_id) === opt.value)
    return (
      <div className="flex items-center gap-2">
        <OptionBadge color="purple">{opt.details}</OptionBadge>
        <span className="flex-1 truncate">{opt.label}</span>
        {oi && (
          <span className="shrink-0 font-mono text-xs text-red-600">{oi.balance}</span>
        )}
      </div>
    )
  }, [counterpartyOpenItems])

  // Resolve current display values
  const counterpartyValue = useMemo(() => {
    const cid = currentOi?.counterparty_id || selectedCounterpartyId
    return cid ? String(cid) : undefined
  }, [currentOi, selectedCounterpartyId])

  const invoiceValue = apxId > 0 ? String(apxId) : undefined

  if (!def || def.fields.length === 0) return null

  const fieldLabels = def.fields.map((f) => f.label || f.field_name)

  return (
    <div className="mt-2 space-y-1">
      {/* Field 0: Counterparty (lookup) */}
      {def.fields.length > 0 && counterpartyOptions.length > 0 && (
        <div className="flex items-center gap-2">
          <span className="w-20 shrink-0 text-xs text-neutral-400">{fieldLabels[0]}:</span>
          <div className="flex-1">
            <FilterableSelect
              value={counterpartyValue}
              onValueChange={(v) => {
                setSelectedCounterpartyId(Number(v))
                onApxSelect(0)
              }}
              options={counterpartyOptions}
              placeholder={`—`}
              searchPlaceholder="Search..."
              triggerClassName="h-7 text-xs border-0 shadow-none px-1 hover:bg-neutral-100"
              renderOption={renderCounterpartyOption}
              renderSelectedValue={(opt) => (
                <div className="flex items-center gap-1.5">
                  <OptionBadge color="green" small>{opt.details}</OptionBadge>
                  <span className="truncate">{opt.label}</span>
                </div>
              )}
            />
          </div>
        </div>
      )}

      {/* Field 1: Invoice / Document (open items filtered by counterparty) */}
      {def.fields.length > 1 && (
        <div className="flex items-center gap-2">
          <span className="w-20 shrink-0 text-xs text-neutral-400">{fieldLabels[1]}:</span>
          <div className="flex-1">
            <FilterableSelect
              value={invoiceValue}
              onValueChange={(v) => {
                const oi = openItems.find((o) => o.apx_id === Number(v))
                if (oi) {
                  onApxSelect(oi.apx_id, oi)
                }
              }}
              options={invoiceOptions}
              placeholder={`—`}
              searchPlaceholder="Search invoice..."
              triggerClassName="h-7 text-xs border-0 shadow-none px-1 hover:bg-neutral-100"
              renderOption={renderInvoiceOption}
              renderSelectedValue={(opt) => (
                <div className="flex items-center gap-1.5">
                  <OptionBadge color="purple" small>{opt.details}</OptionBadge>
                  <span className="truncate">{opt.label}</span>
                </div>
              )}
            />
          </div>
        </div>
      )}

      {/* Field 2: Date (auto-filled, editable) */}
      {def.fields.length > 2 && (
        <div className="flex items-center gap-2">
          <span className="w-20 shrink-0 text-xs text-neutral-400">{fieldLabels[2]}:</span>
          <Input
            value={currentOi?.doc_date || docDate}
            onChange={(e) => onDocChange(docNom, e.target.value)}
            placeholder="DD.MM.YYYY"
            className="h-7 w-32 border-0 px-1 text-xs shadow-none hover:bg-neutral-100 focus:border focus:shadow-sm"
          />
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Colored badge for dropdown options
// ---------------------------------------------------------------------------

const BADGE_COLORS = {
  blue: 'bg-blue-100 text-blue-700 border-blue-200',
  green: 'bg-green-100 text-green-700 border-green-200',
  purple: 'bg-purple-100 text-purple-700 border-purple-200',
} as const

function OptionBadge({
  color,
  small,
  children,
}: {
  color: keyof typeof BADGE_COLORS
  small?: boolean
  children: React.ReactNode
}) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center rounded-md border font-mono font-medium',
        BADGE_COLORS[color],
        small ? 'px-1 py-0 text-[10px]' : 'px-1.5 py-0.5 text-[11px]'
      )}
    >
      {children}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Click-to-edit inline field
// ---------------------------------------------------------------------------

function ClickToEdit({
  value,
  onChange,
  placeholder,
  maxLength,
  type = 'text',
  className: extraClass,
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  maxLength?: number
  type?: 'text' | 'number'
  className?: string
}) {
  const [editing, setEditing] = useState(false)

  if (editing) {
    return (
      <Input
        type={type}
        step={type === 'number' ? '0.01' : undefined}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => setEditing(false)}
        onKeyDown={(e) => { if (e.key === 'Enter') setEditing(false) }}
        placeholder={placeholder}
        maxLength={maxLength}
        className={cn('h-7 border-0 px-1 text-sm shadow-none focus:border focus:shadow-sm', extraClass)}
        autoFocus
      />
    )
  }

  return (
    <button
      type="button"
      className={cn(
        'rounded px-1 py-1 text-left text-sm hover:bg-neutral-100',
        !value && 'text-neutral-300 italic',
        extraClass
      )}
      onClick={() => setEditing(true)}
    >
      {value || placeholder || '—'}
    </button>
  )
}
